import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { setAuth } from '@/lib/auth'

function validateEmail(email: string, t: (key: string) => string): string | null {
  if (!email) return t('auth.errors.emailRequired')
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!re.test(email)) return t('auth.errors.emailInvalid')
  return null
}

function validatePassword(pw: string, t: (key: string) => string): string | null {
  if (!pw) return t('auth.errors.passwordRequired')
  if (pw.length < 8) return t('auth.errors.passwordMinLength')
  if (!/[a-zA-Z]/.test(pw)) return t('auth.errors.passwordLetters')
  if (!/[0-9]/.test(pw)) return t('auth.errors.passwordNumbers')
  return null
}

/**
 * 落地页登录弹窗（OPT-031）：账号密码 + 忘记密码 + 注册
 * view: 'login' | 'forgot' | 'register'
 */
export function LoginDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [view, setView] = useState<'login' | 'forgot' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; name?: string; password?: string }>({})
  const [sent, setSent] = useState(false)
  // OPT-032：Google 登录配置（打开弹窗时拉取一次）
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [googleAuthUrl, setGoogleAuthUrl] = useState<string | null>(null)
  // 登录失败锁定（前后端双保险）：lockedUntil 驱动倒计时，failCount 为前端本地计数
  const [lockedUntil, setLockedUntil] = useState(0)
  const [failCount, setFailCount] = useState(0)
  const [now, setNow] = useState(Date.now())

  // 打开时探测 Google 是否启用（未配置则隐藏按钮）
  useEffect(() => {
    if (!open) return
    let cancelled = false
    api.googleConfig().then((res) => {
      if (!cancelled) { setGoogleEnabled(!!res.enabled); setGoogleAuthUrl(res.authUrl || null) }
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open])

  // 锁定倒计时：锁定期内每秒刷新 now，驱动剩余秒数渲染
  useEffect(() => {
    if (!open || lockedUntil <= Date.now()) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [open, lockedUntil])

  const lockRemaining = Math.max(0, Math.ceil((lockedUntil - now) / 1000))
  const isLocked = lockRemaining > 0

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLocked) return // 锁定期内不发请求
    setError('')
    const emailErr = validateEmail(email, t)
    if (emailErr) { setFieldErrors({ email: emailErr }); return }
    if (!password) { setError(t('auth.errors.passwordRequired')); return }
    setFieldErrors({})
    setLoading(true)
    try {
      const { user } = await api.login({ email, password })
      setAuth(user)
      setFailCount(0)
      setLockedUntil(0)
      onClose()
      navigate('/app')
    } catch (err: unknown) {
      const e = err as Error & { code?: string; retryAfterSeconds?: number }
      if (e?.code === 'RATE_LIMITED' && typeof e.retryAfterSeconds === 'number') {
        // 后端锁定：服务器为权威，进入倒计时
        setLockedUntil(Date.now() + e.retryAfterSeconds * 1000)
        setNow(Date.now())
        setFailCount(0)
        setError(t('auth.errors.loginLocked', { seconds: e.retryAfterSeconds }))
        return
      }
      // 普通失败：前端本地计数，连续 5 次同样锁定 60 秒
      const next = failCount + 1
      if (next >= 5) {
        setLockedUntil(Date.now() + 60_000)
        setNow(Date.now())
        setFailCount(0)
        setError(t('auth.errors.loginLocked', { seconds: 60 }))
      } else {
        setFailCount(next)
        setError(e instanceof Error ? e.message : t('auth.errors.loginFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const emailErr = validateEmail(email, t)
    if (emailErr) { setFieldErrors({ email: emailErr }); return }
    setFieldErrors({})
    setLoading(true)
    try {
      await api.forgotPassword(email)
      setSent(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('common.failed'))
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const errs: { name?: string; email?: string; password?: string } = {}
    if (!name.trim()) errs.name = t('auth.errors.nameRequired')
    const emailErr = validateEmail(email, t)
    if (emailErr) errs.email = emailErr
    const pwErr = validatePassword(password, t)
    if (pwErr) errs.password = pwErr
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return }
    setFieldErrors({})
    setLoading(true)
    try {
      const { user } = await api.register({ email, password, name: name.trim() })
      setAuth(user)
      onClose()
      navigate('/app')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('auth.errors.registerFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setTimeout(() => { setView('login'); setSent(false); setError(''); setPassword('') }, 200) } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{view === 'login' ? t('auth.login') : t('auth.forgotTitle')}</DialogTitle>
          <DialogDescription>
            {view === 'forgot' && !sent ? t('auth.forgotHint') : view === 'register' ? t('auth.register') : view === 'login' ? t('landing.loginHint') : ''}
          </DialogDescription>
        </DialogHeader>

        {view === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4" noValidate>
            {isLocked ? (
              <div className="p-3 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded">
                {t('auth.errors.loginLocked', { seconds: lockRemaining })}
              </div>
            ) : (
              error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="ld-email">{t('auth.email')}</Label>
              <Input id="ld-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors({}) }} />
              {fieldErrors.email && <p className="text-sm text-red-600">{fieldErrors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ld-password">{t('auth.password')}</Label>
              <Input id="ld-password" type="password" placeholder={t('auth.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" className="w-full" disabled={loading || isLocked}>{loading ? t('common.loading') : t('auth.loginButton')}</Button>
            {/* OPT-032：Google 登录（未配置时隐藏） */}
            {googleEnabled && googleAuthUrl && (
              <a
                href={googleAuthUrl}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 py-2 text-sm font-medium hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.2h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.7z"/>
                  <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.9-5.1l-3.8 2.9C3.3 21.3 7.3 24 12 24z"/>
                  <path fill="#FBBC05" d="M5.1 14.3c-.3-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.8-2.9z"/>
                  <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.8l3.8 2.9c1-3 3.7-5 6.9-5z"/>
                </svg>
                {t('auth.googleLogin')}
              </a>
            )}
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setView('forgot'); setError('') }} className="text-primary hover:underline">{t('auth.forgotLink')}</button>
              <button type="button" onClick={() => { setView('register'); setError(''); setFieldErrors({}) }} className="text-primary hover:underline">{t('auth.register')}</button>
            </div>
          </form>
        ) : view === 'forgot' ? (
          sent ? (
            <div className="p-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded">{t('auth.forgotSent')}</div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4" noValidate>
              {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">{error}</div>}
              <div className="space-y-2">
                <Label htmlFor="ld-forgot-email">{t('auth.email')}</Label>
                <Input id="ld-forgot-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors({}) }} />
                {fieldErrors.email && <p className="text-sm text-red-600">{fieldErrors.email}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={loading}>{loading ? t('common.loading') : t('auth.forgotButton')}</Button>
              <button type="button" onClick={() => { setView('login'); setError('') }} className="text-sm text-primary hover:underline">{t('common.cancel')}</button>
            </form>
          )
        ) : (
          <form onSubmit={handleRegister} className="space-y-4" noValidate>
            {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">{error}</div>}
            <div className="space-y-2">
              <Label htmlFor="ld-name">{t('auth.name')}</Label>
              <Input id="ld-name" type="text" placeholder={t('auth.namePlaceholder')} value={name} onChange={(e) => { setName(e.target.value); if (fieldErrors.name) setFieldErrors({}) }} />
              {fieldErrors.name && <p className="text-sm text-red-600">{fieldErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ld-reg-email">{t('auth.email')}</Label>
              <Input id="ld-reg-email" type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors({}) }} />
              {fieldErrors.email && <p className="text-sm text-red-600">{fieldErrors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ld-reg-password">{t('auth.password')}</Label>
              <Input id="ld-reg-password" type="password" placeholder={t('auth.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} />
              {fieldErrors.password && <p className="text-sm text-red-600">{fieldErrors.password}</p>}
              <p className="text-xs text-muted-foreground">{t('auth.passwordHint')}</p>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>{loading ? t('common.loading') : t('auth.registerButton')}</Button>
            <button type="button" onClick={() => { setView('login'); setError(''); setFieldErrors({}) }} className="text-sm text-primary hover:underline">{t('auth.hasAccount')}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
